"use client"

import { ChevronDown, MessageCircle } from "lucide-react"
import React from "react"

export type FaqItem = {
  id: string
  q: string
  /** Markdown-style answer body — supports `**bold**`, `[label](url)`, and `-` / `1.` lists. */
  a: string
}

type Props = {
  /** Localized section title (e.g. "Troubleshooting"). */
  title: string
  items: FaqItem[]
}

/**
 * FAQ / troubleshooting accordion. Each item renders inside a native
 * `<details>` so the page works even with JavaScript disabled (important
 * for SEO — Google crawlers prefer HTML-native expand/collapse).
 *
 * Content comes from `install-guide.json` `troubleshooting` array, which
 * is already translated across 49 locales. We preserve the existing
 * markdown so translations don't need to be re-run.
 */
export function FaqSection({ title, items }: Props) {
  if (items.length === 0) return null

  return (
    <section className="grid gap-3" aria-label={title}>
      <h2 className="px-1 text-[22px] font-extrabold tracking-tight text-slate-950 md:text-[26px]">
        {title}
      </h2>
      <div className="grid gap-2">
        {items.map((item) => (
          <details
            key={item.id}
            className="group overflow-hidden rounded-2xl border border-stone-200 bg-white open:border-orange-200"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-stone-50 [&::-webkit-details-marker]:hidden">
              <span className="flex items-center gap-3">
                <span
                  aria-hidden
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600"
                >
                  <MessageCircle className="h-4 w-4" />
                </span>
                <span className="text-[15px] font-bold text-slate-950">
                  {item.q}
                </span>
              </span>
              <ChevronDown
                className="h-5 w-5 shrink-0 text-stone-500 transition-transform group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <div className="border-t border-stone-100 p-5 text-[14px] leading-7 text-slate-700">
              {renderAnswer(item.a)}
            </div>
          </details>
        ))}
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Minimal markdown renderer — bold + link + bulleted / numbered lists.
// Kept inline because the rest of the codebase does the same (no markdown
// lib dependency). Translation files use these conventions everywhere.
// ─────────────────────────────────────────────────────────────────────

function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0
  while (remaining.length > 0) {
    const lm = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/)
    const bm = remaining.match(/\*\*([^*]+)\*\*/)
    let next: { type: "link" | "bold"; match: RegExpMatchArray } | null = null
    if (lm && (!bm || (lm.index ?? 0) <= (bm.index ?? 0))) {
      next = { type: "link", match: lm }
    } else if (bm) {
      next = { type: "bold", match: bm }
    }
    if (!next) {
      parts.push(remaining)
      break
    }
    const idx = next.match.index ?? 0
    if (idx > 0) parts.push(remaining.slice(0, idx))
    if (next.type === "link") {
      parts.push(
        <a
          key={key++}
          href={next.match[2]}
          className="text-orange-600 underline decoration-orange-200 underline-offset-2 hover:decoration-orange-600"
        >
          {next.match[1]}
        </a>
      )
    } else {
      parts.push(
        <strong key={key++} className="font-semibold text-stone-900">
          {next.match[1]}
        </strong>
      )
    }
    remaining = remaining.slice(idx + next.match[0].length)
  }
  return parts
}

function renderAnswer(text: string): React.ReactNode {
  const lines = text.split("\n")
  const nodes: React.ReactNode[] = []
  let listBuf: { type: "ul" | "ol"; items: string[] } | null = null

  const flush = () => {
    if (!listBuf) return
    const Tag = listBuf.type === "ul" ? "ul" : "ol"
    const className =
      listBuf.type === "ol"
        ? "my-2 list-decimal space-y-1.5 pl-5"
        : "my-2 list-disc space-y-1.5 pl-5"
    nodes.push(
      <Tag key={`list-${nodes.length}`} className={className}>
        {listBuf.items.map((line, i) => (
          <li key={i}>{renderInline(line)}</li>
        ))}
      </Tag>
    )
    listBuf = null
  }

  for (const line of lines) {
    if (line.trim() === "") {
      flush()
      continue
    }
    const ol = line.match(/^\d+\.\s+(.+)$/)
    const ul = line.match(/^-\s+(.+)$/)
    if (ol) {
      if (listBuf?.type !== "ol") {
        flush()
        listBuf = { type: "ol", items: [] }
      }
      listBuf.items.push(ol[1])
    } else if (ul) {
      if (listBuf?.type !== "ul") {
        flush()
        listBuf = { type: "ul", items: [] }
      }
      listBuf.items.push(ul[1])
    } else {
      flush()
      nodes.push(
        <p key={`p-${nodes.length}`} className="my-2 first:mt-0 last:mb-0">
          {renderInline(line)}
        </p>
      )
    }
  }
  flush()

  return nodes
}
